import { summarizeMediaStreamTrack } from "./utils";
import { GatherEventEmitter } from "gather-common-including-video/dist/src/public/GatherEventEmitter";
import { Logger } from "./Logger";
export var PlayerMediaSourceEvent;
(function (PlayerMediaSourceEvent) {
    PlayerMediaSourceEvent["AddTrack"] = "addTrack";
    PlayerMediaSourceEvent["RemoveTrack"] = "removeTrack";
    PlayerMediaSourceEvent["TrackMuteStateChanged"] = "trackMuteStateChanged";
    PlayerMediaSourceEvent["Play"] = "play";
})(PlayerMediaSourceEvent || (PlayerMediaSourceEvent = {}));
export class PlayerMediaSource extends GatherEventEmitter {
    settings = {
        playerId: "",
        isScreen: false,
        isSelfView: false,
    };
    audioTrack;
    videoTrack;
    muted = {
        audio: new Set(),
        video: new Set(),
    };
    get summary() {
        return this.summarize();
    }
    constructor(settings) {
        super();
        this.settings = { ...this.settings, ...settings };
    }
    get playerId() {
        return this.settings.playerId;
    }
    get isScreen() {
        return this.settings.isScreen;
    }
    get isSelfView() {
        return this.settings.isSelfView;
    }
    clear() {
        if (this.audioTrack) {
            this.removeTrack("audio");
        }
        if (this.videoTrack) {
            this.removeTrack("video");
        }
    }
    addTrack(track) {
        const kind = track.kind;
        if (track.readyState === "ended") {
            Logger.error(`attempt to add a ${track.kind} track that is already ended`);
            return;
        }
        if (kind === "audio" && this.audioTrack !== track) {
            this.audioTrack = track;
        }
        else if (kind === "video" && this.videoTrack !== track) {
            this.videoTrack = track;
        }
        else {
            return;
        }
        const consumerKind = kind === "audio" ? (this.isScreen ? "sound" : "audio") : this.isScreen ? "screen" : "video";
        this.publishEvent(PlayerMediaSourceEvent.AddTrack, consumerKind, track);
        this.dispatchMute();
    }
    removeTrack(kind) {
        if (kind === "audio" && this.audioTrack) {
            this.audioTrack = undefined;
            this.muted.audio = new Set();
        }
        else if (kind === "video" && this.videoTrack) {
            this.videoTrack = undefined;
            this.muted.video = new Set();
        }
        else {
            return;
        }
        const consumerKind = kind === "audio" ? (this.isScreen ? "sound" : "audio") : this.isScreen ? "screen" : "video";
        this.publishEvent(PlayerMediaSourceEvent.RemoveTrack, consumerKind);
    }
    setMute(side, kind, muted) {
        if (muted) {
            this.muted[kind].add(side);
        }
        else {
            this.muted[kind].delete(side);
        }
        this.dispatchMute();
    }
    summarize() {
        const audioTracks = this.audioTrack ? [summarizeMediaStreamTrack(this.audioTrack)] : [];
        const videoTracks = this.videoTrack ? [summarizeMediaStreamTrack(this.videoTrack)] : [];
        const muted = {
            audio: Array.from(this.muted.audio),
            video: Array.from(this.muted.video),
        };
        return {
            audioTracks,
            videoTracks,
            muted,
        };
    }
    toJSON() {
        return { summary: this.summarize() };
    }
    isRearFacing() {
        if (this.videoTrack) {
            const capabilities = this.videoTrack.getCapabilities();
            return capabilities.facingMode?.includes("environment") ?? false;
        }
        return false;
    }
    dispatchMute() {
        Object.entries(this.muted).forEach(([kind, muteSet]) => {
            const track = kind === "audio" ? this.audioTrack : this.videoTrack;
            const consumerKind = kind === "audio" ? (this.isScreen ? "sound" : "audio") : this.isScreen ? "screen" : "video";
            const enabled = muteSet.size === 0;
            if (track && track.enabled !== enabled) {
                track.enabled = enabled;
            }
            this.publishEvent(PlayerMediaSourceEvent.TrackMuteStateChanged, consumerKind, !enabled);
        });
    }
    get hasVideoTrack() {
        return !!this.videoTrack;
    }
    get hasAudioTrack() {
        return !!this.audioTrack;
    }
    play() {
        this.publishEvent(PlayerMediaSourceEvent.Play);
    }
}
//# sourceMappingURL=PlayerMediaSource.js.map